import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Save, Package } from 'lucide-react';
import { packagingService } from '../services/packagingService';
import { PackagingRequestDTO, PackagingResponseDTO } from '../types/packaging';
import { alertService } from '../services/alertService';

const PackagingInfoPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const isEditing = Boolean(id);

  const [form, setForm] = useState<PackagingRequestDTO>({
    descricao: '',
    sigla: '',
    ativo: true,
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!isEditing) return;
      try {
        setLoading(true);
        const response = await packagingService.getById(id as string);
        if (response.success && response.data) {
          const data = response.data as PackagingResponseDTO;
          setForm({ descricao: data.descricao, sigla: data.sigla, ativo: data.ativo });
        } else {
          alertService.error('Não foi possível carregar a embalagem');
        }
      } catch (e) {
        console.error('Erro ao carregar embalagem', e);
        alertService.error('Erro ao carregar embalagem');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [id, isEditing]);

  const handleSave = async () => {
    try {
      setLoading(true);
      if (isEditing) {
        const resp = await packagingService.update(id as string, form);
        if (resp.success) {
          alertService.success('Embalagem atualizada com sucesso');
          navigate('/embalagens');
        } else {
          alertService.error('Erro ao atualizar embalagem');
        }
      } else {
        const resp = await packagingService.create(form);
        if (resp.success) {
          alertService.success('Embalagem criada com sucesso');
          navigate('/embalagens');
        } else {
          alertService.error('Erro ao criar embalagem');
        }
      }
    } catch (e) {
      console.error('Erro ao salvar embalagem', e);
      alertService.error('Erro ao salvar embalagem');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-3">
          <Package className="h-8 w-8 text-red-600" />
          <div>
            <h1 className="text-2xl font-bold text-gray-900">{isEditing ? 'Editar Embalagem' : 'Nova Embalagem'}</h1>
            <p className="text-gray-600">{isEditing ? 'Atualize os dados da embalagem' : 'Cadastre uma nova embalagem'}</p>
          </div>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => navigate('/embalagens')}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-md hover:bg-gray-200 flex items-center gap-2"
          >
            <ArrowLeft size={18} /> Voltar
          </button>
          <button
            onClick={handleSave}
            disabled={loading || !form.descricao || !form.sigla}
            className="bg-red-600 text-white px-4 py-2 rounded-md hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
          >
            <Save size={18} /> Salvar
          </button>
        </div>
      </div>

      {/* Form */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">Descrição</label>
            <input
              type="text"
              value={form.descricao}
              onChange={(e) => setForm({ ...form, descricao: e.target.value })}
              placeholder="Ex.: Caixa 1kg"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Sigla</label>
            <input
              type="text"
              value={form.sigla}
              onChange={(e) => setForm({ ...form, sigla: e.target.value })}
              placeholder="Ex.: CX1"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Status</label>
            <select
              value={form.ativo ? 'true' : 'false'}
              onChange={(e) => setForm({ ...form, ativo: e.target.value === 'true' })}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-red-500"
            >
              <option value="true">Ativo</option>
              <option value="false">Inativo</option>
            </select>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PackagingInfoPage;